import React, { useEffect, useState } from "react";
import { Col } from "reactstrap";
import { MapPin, Phone } from "react-feather";
import { Toast } from "utility/helpers";
import { capitalize } from "lodash";
import { useLocation, useNavigate } from "react-router";
import { genericPost, genericPut } from "services/services.general";
import useSectors from "hooks/useSectors";
import { HeaderText } from "assets/js/Styles";
import styled from "styled-components";
import {
  TESDA_COURSE_PROGRAMS,
  TESDA_PROGRAM_STEP_TOOLKIT_COST,
} from "constants/agency";
import { getHeaderText, validateTVICourseForm } from "./utility";
import TVICourseFormSection from "./TVICourseFormSection";

const HeaderTextSection = styled.div`
  margin-top: 24px;
  display: grid;
  justify-content: space-between;
  grid-auto-flow: column;
  align-items: center;

  h2 {
    margin-bottom: 0;
  }

  a {
    text-decoration: none;
  }

  @media (max-width: 575.98px) {
    grid-template-columns: 1fr 50px;

    a {
      display: flex;
      justify-content: flex-end;
    }

    padding-top: 4rem;
  }
`;

const Wrapper = styled.div`
  margin-top: 50px;
`;

const StyledMapIcon = styled(MapPin)`
  margin-right: 8px;
`;

const StyledPhoneIcon = styled(Phone)`
  margin-right: 8px;
`;

const TVICourseForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const row = location.state?.row || null;
  const action = location.state?.action || null;
  const tvi = location.state?.tvi || null;

  const [isLoading, setIsLoading] = useState(false);

  const { options: sectorOptions } = useSectors();

  const [form, setForm] = useState({
    name: null,
    courseCode: 0,
    tesdaQualificationId: null,
    trainingCost: 0,
    assessmentCost: 0,
    trainingSupportFund: 0,
    sectorId: null,
    toolKitCost: 0,
    entrepFee: TESDA_PROGRAM_STEP_TOOLKIT_COST,
    projectId: null,
  });

  const saveData = (formData) => {
    if (tvi) {
      genericPost({
        url: `/tvi/${tvi.id}/create-course`,
        formData,
        callback: ({ isSuccess }) => {
          setIsLoading(false);

          if (isSuccess) {
            navigate("/settings/tesda/courses");

            Toast({
              type: 1,
              content: "Successfully created course.",
            });
          } else {
            Toast({
              type: 4,
              content: "An error occurred.",
            });
          }
        },
      });
    } else {
      genericPost({
        url: "/course",
        formData,
        callback: ({ isSuccess }) => {
          setIsLoading(false);

          if (isSuccess) {
            navigate("/settings/tesda/courses");

            Toast({
              type: 1,
              content: "Successfully created course.",
            });
          } else {
            Toast({
              type: 4,
              content: "An error occurred.",
            });
          }
        },
      });
    }
  };

  const updateData = (formData, id) => {
    genericPut({
      url: `/course/${id}`,
      formData,
      callback: ({ isSuccess }) => {
        if (isSuccess) {
          setTimeout(() => {
            Toast({
              type: 1,
              content: "Successfully updated course.",
            });
          }, 1500);

          navigate("/settings/tesda/courses");
        } else {
          Toast({
            type: 4,
            content: "An error occurred.",
          });
        }
      },
    });
  };

  const handleSubmit = (values) => {
    const isValid = validateTVICourseForm({
      form: values,
    });

    if (typeof isValid === "boolean") {
      setIsLoading(true);

      const formData = {
        name: values.name,
        courseCode: values.courseCode,
        tesdaQualificationId: values.tesdaQualificationId,
        trainingCost: values.trainingCost,
        assessmentCost: values.assessmentCost,
        trainingSupportFund: values.trainingSupportFund,
        sectorId: values.sectorId?.value,
        projectId: values.projectId?.value,
        toolKitCost: values.toolKitCost,
      };

      if (row === null) {
        saveData(formData);
      } else {
        updateData(formData, row.id);
      }
    } else {
      Toast({
        type: 4,
        content: isValid,
      });
    }
  };

  const setFormData = () => {
    const defaultSector = sectorOptions.find(
      (sector) => sector.value === row.sectorId
    );

    const selectedProjectType = TESDA_COURSE_PROGRAMS.find(
      (courseType) => courseType.value === row.projectId
    );

    setForm({
      ...form,
      name: row.name,
      courseCode: row.courseCode,
      tesdaQualificationId: row.tesdaQualificationId,
      trainingCost: row.trainingCost,
      assessmentCost: row.assessmentCost,
      trainingSupportFund: row.trainingSupportFund,
      sectorId: defaultSector,
      projectId: selectedProjectType,
      toolKitCost: row.toolKitCost,
    });
  };

  useEffect(() => {
    if (row && sectorOptions.length > 0) {
      setFormData();
    }
  }, [row, sectorOptions]);

  return (
    <Wrapper>
      <Col className="container">
        <HeaderTextSection>
          <HeaderText>{getHeaderText(action)}</HeaderText>
        </HeaderTextSection>
        {tvi !== null && (
          <div>
            <h4>{tvi?.name}</h4>

            <span>
              <StyledMapIcon />
              {/* eslint-disable-next-line max-len */}
              {`${capitalize(tvi?.municipality?.toLowerCase())}, ${
                tvi?.province
              }, ${tvi?.region}`}
            </span>

            {tvi?.contactNumber && (
              <span>
                <StyledPhoneIcon />
                {tvi?.contactNumber}
              </span>
            )}
          </div>
        )}

        <TVICourseFormSection
          form={form}
          action={action}
          isLoading={isLoading}
          sectorOptions={sectorOptions}
          handleSubmit={handleSubmit}
        />
      </Col>
    </Wrapper>
  );
};

export default TVICourseForm;
